import { Component } from '@theme/component';
import { ThemeEvents, QuickAddSelectEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { openQuickAddDrawer, cancelQuickAddDrawer } from '@theme/quick-add-opener';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * How long the add waits for the section to come back after the picked size has been applied
 * to the page's variant picker. Past that it goes through anyway: `product-form-component`
 * already points at the right variant from `variant:selected`, so a slow response must not
 * leave the visitor with a drawer that did nothing.
 */
const VARIANT_UPDATE_TIMEOUT = 3000;

/**
 * Turns the product page's add-to-cart button into the mobile entry point of the size drawer.
 *
 * Rendered by the Size selector block when its mobile drawer setting is on. The block hides
 * itself on mobile; the button then invites the visitor to pick a size, opens the same drawer
 * the sticky bar uses, and adds to cart once a size comes back.
 *
 * Nothing is rendered here: the element only carries the product data and the listeners. The
 * block's stylesheet reads two attributes, both rendered by the server so the first paint is
 * already the right one -- this component only ever takes them away:
 *   - `data-pending` on the element: no size picked yet, so the button announces the choice.
 *   - `data-size-drawer-fallback` on the section: the drawer could not be fetched, so the
 *     inline picker comes back. The visitor must never be left with no way to pick a size.
 *
 * @extends {Component}
 */
class SizeDrawerCtaComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** Cleans up the wait for the section response, which outlives no more than one add. */
  #pendingAddController = new AbortController();

  /** @type {boolean} */
  #awaitingSelection = false;

  /**
   * Identifies the latest drawer opening. Two taps in a row abort the first one, which then
   * comes back as `cancelled` while the second drawer is already on screen: without this
   * token the stale call would clear the wait the new one is relying on.
   *
   * @type {number}
   */
  #selectionToken = 0;

  /** @type {boolean} */
  #picked = false;

  connectedCallback() {
    super.connectedCallback();

    const section = this.#section;
    if (!section) {
      // Nothing to listen on: the button goes back to being a plain add to cart rather than
      // promising a choice nobody would catch.
      this.#giveUpTakeover();
      return;
    }

    const { signal } = this.#abortController;

    section.addEventListener('click', this.#handleAddToCartClick, { signal });
    section.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    // The drawer is inserted into `body`, outside of the section: the event is listened for on
    // `document` rather than on the element, or it would never reach us.
    document.addEventListener(QuickAddSelectEvent.eventName, this.#handleQuickAddSelect, { signal });

    // The block's guard posts the fallback when this file takes too long to arrive. The
    // takeover is on now, so the inline picker steps back.
    section.removeAttribute('data-size-drawer-fallback');

    this.#reflectPendingState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#pendingAddController.abort();
    // Only its own opening: aborting unconditionally would kill a product card's drawer.
    cancelQuickAddDrawer(this);

    this.#section?.removeAttribute('data-size-drawer-fallback');
  }

  /**
   * The section the block lives in: what the listeners hang off, and what carries the
   * fallback state once the takeover gives up.
   * @returns {HTMLElement | null}
   */
  get #section() {
    return /** @type {HTMLElement | null} */ (this.closest('.shopify-section'));
  }

  /**
   * The product page's own add-to-cart button.
   *
   * Scoped to the buy buttons block on purpose: the bundle block and the recommendations put
   * product cards in the same section, each with an add button of their own.
   *
   * @returns {HTMLButtonElement | null}
   */
  get #addToCartButton() {
    const section = this.#section;
    const productId = this.dataset.productId;
    if (!section || !productId) return null;

    return /** @type {HTMLButtonElement | null} */ (
      section.querySelector(
        `.buy-buttons-block product-form-component[data-product-id="${CSS.escape(
          productId
        )}"] [data-product-card-standalone-add-to-cart] button[ref="addToCartButton"]`
      )
    );
  }

  /**
   * Reflects the "no size picked yet" state, which is what flips the button's label.
   */
  #reflectPendingState() {
    this.toggleAttribute('data-pending', !this.#picked);
  }

  /**
   * Records that the visitor has settled on a size, from the drawer or from the page itself.
   */
  #markPicked() {
    if (this.#picked) return;

    this.#picked = true;
    this.#reflectPendingState();
  }

  /**
   * Gives the page back its inline picker when the drawer cannot be shown.
   *
   * The button falls back to a plain add to cart, so the size has to be pickable somewhere.
   */
  #giveUpTakeover() {
    this.#picked = true;
    this.#section?.toggleAttribute('data-size-drawer-fallback', true);
    this.#reflectPendingState();
  }

  /**
   * Sends the first tap on the add-to-cart button to the size drawer instead of the cart.
   * @param {Event} event - The click event.
   */
  #handleAddToCartClick = (event) => {
    if (this.#picked) return;
    // Desktop keeps the inline picker: the button has nothing to stand in for there.
    if (!isMobileBreakpoint()) return;
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('button[name="add"]');
    if (!button || button !== this.#addToCartButton) return;

    // The button submits the form: cancelling the click is what keeps the line out of the cart.
    event.preventDefault();
    event.stopPropagation();

    this.#openSizeDrawer();
  };

  /**
   * Opens the size drawer, the same one the sticky bar and the product cards use.
   *
   * `select` mode: the drawer hands the picked variant back instead of adding it, so the
   * page's variant picker stays the single source of truth for what the add button sends.
   * `cta: 'add'` only settles the drawer's own button label, since here a confirmed size
   * does land in the cart. `compact` drops everything the product page already shows, and the
   * CTA with it: tapping a size is the validation.
   */
  async #openSizeDrawer() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    const token = ++this.#selectionToken;
    this.#awaitingSelection = true;

    const status = await openQuickAddDrawer({
      productUrl,
      variantId: this.dataset.currentVariantId,
      mode: 'select',
      cta: 'add',
      layout: 'compact',
      owner: this,
    });

    // A newer tap has taken over the wait in the meantime. It owns the drawer now, so this
    // stale call must not touch the flag: clearing it would make the visitor's pick be ignored.
    if (token !== this.#selectionToken) return;

    if (status === 'opened') {
      this.#stopWaitingOnClose(token);
      return;
    }

    this.#awaitingSelection = false;

    // The tap was intercepted, so it has to lead somewhere.
    if (status === 'unavailable') {
      // Nothing left to pick: the preselected variant is the only one, add it.
      this.#markPicked();
      this.#addToCart();
    } else if (status === 'error') {
      // Never add a size the visitor has not seen: hand the choice back to the inline picker.
      this.#giveUpTakeover();
    }
  }

  /**
   * Closes the wait when the drawer is dismissed without a pick.
   *
   * Left open, the page would keep listening on `document` and would apply a size picked
   * later from another opener -- a bundle card, a recommendation -- for the same product.
   * `dialog:close` does not bubble, so it is listened for on the drawer itself; the drawer is
   * looked up in the document because `openQuickAddDrawer` guarantees a single one at a time.
   *
   * @param {number} token - The opening this wait belongs to.
   */
  #stopWaitingOnClose(token) {
    const drawer = document.querySelector('quick-add-drawer-component');
    if (!drawer) return;

    drawer.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        // A pick already closed the wait, or a newer opening reopened one: nothing to clear.
        if (token !== this.#selectionToken) return;
        this.#awaitingSelection = false;
      },
      { once: true, signal: this.#abortController.signal }
    );
  }

  /**
   * Applies the size picked in the drawer to the page, then adds it to the cart.
   * @param {Event} event - The quick add select event.
   */
  #handleQuickAddSelect = (event) => {
    if (!this.#awaitingSelection) return;

    const detail = /** @type {any} */ (event).detail;
    // The drawer is shared with the product cards: only the answer to our own opening
    // counts, and only for the product of the page.
    if (String(detail?.data?.productId ?? '') !== this.dataset.productId) return;

    this.#awaitingSelection = false;
    this.#markPicked();

    // A size already checked in the picker leaves it untouched, so no section update follows
    // and there is nothing to wait for.
    if (this.#selectOptionValues(detail?.resource?.options)) {
      this.#addToCartOnVariantUpdate();
    } else {
      this.#addToCart();
    }
  };

  /**
   * Checks the matching values in the page's variant picker, which then updates the whole
   * section -- price, availability and the buy buttons included -- through its own flow.
   *
   * The drawer only renders the size option when it recognises one, and the takeover does not
   * exist without one: in practice the loop runs over a single value.
   *
   * @param {Record<string, string> | undefined} options - Picked values, keyed by option position.
   * @returns {boolean} Whether a value actually changed, and a section update is on its way.
   */
  #selectOptionValues(options) {
    const productId = this.dataset.productId;
    if (!options || !productId) return false;

    const pickers = document.querySelectorAll(`variant-picker[data-product-id="${CSS.escape(productId)}"]`);
    if (!pickers.length) return false;

    let changed = false;

    for (const [position, value] of Object.entries(options)) {
      for (const picker of pickers) {
        const input = /** @type {HTMLInputElement | undefined} */ (
          [...picker.querySelectorAll(`input[data-input-id^="${CSS.escape(position)}-"]`)].find(
            (candidate) => /** @type {HTMLInputElement} */ (candidate).value === value
          )
        );

        if (!input || input.checked) continue;

        // A click, not a `checked = true`: the picker listens for `change`, and this is the
        // very path a visitor takes when picking a size from the page.
        input.click();
        changed = true;
        break;
      }
    }

    return changed;
  }

  /**
   * Adds to cart once the section has come back with the picked size.
   *
   * The buy buttons are morphed by `product-form-component` on the same event, and listener
   * order between the two is not guaranteed: the click waits a frame so it lands on the
   * refreshed button rather than on the one being replaced.
   */
  #addToCartOnVariantUpdate() {
    const section = this.#section;
    if (!section) return;

    this.#pendingAddController.abort();
    this.#pendingAddController = new AbortController();
    const { signal } = this.#pendingAddController;

    const add = () => {
      this.#pendingAddController.abort();
      requestAnimationFrame(() => this.#addToCart());
    };

    section.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        // Bundle and upsell cards live in the same section: their own updates are not ours.
        if (/** @type {any} */ (event).detail?.data?.productId !== this.dataset.productId) return;
        add();
      },
      { signal }
    );

    // A lost request must not swallow the add: the form already carries the picked variant,
    // which `product-form-component` set on `variant:selected`.
    const timeout = setTimeout(add, VARIANT_UPDATE_TIMEOUT);
    signal.addEventListener('abort', () => clearTimeout(timeout), { once: true });
  }

  /**
   * Sends the page's own add-to-cart button. The takeover is over by then, so the click goes
   * through to the form.
   */
  #addToCart() {
    const button = this.#addToCartButton;
    if (!button || button.disabled) return;

    button.click();
  }

  /**
   * Takes note of a size picked outside of the drawer -- the inline picker on a wider screen,
   * the sticky bar -- so the button stops offering a choice already made.
   * @param {CustomEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    // The product cards of the bundle block and of the upsell live in the same section: their
    // own variant choice bubbles up here and must not be taken for the page's own.
    const source = event.target instanceof HTMLElement ? event.target : null;
    if (!source || source.closest('product-card')) return;
    if (source.dataset.productId && source.dataset.productId !== this.dataset.productId) return;

    const { id: optionValueId, variantId } = event.detail.resource ?? {};

    // `resource.id` is the option value id, not the variant id: storing it here would send the
    // drawer a `?variant=` Shopify cannot resolve.
    if (variantId) this.dataset.currentVariantId = variantId;

    if (this.#isSizeOptionValue(optionValueId)) this.#markPicked();
  };

  /**
   * Whether an option value picked in the page's variant picker is a size.
   *
   * Only the size option counts: changing another option must not pass for a size the visitor
   * never picked, it stays the one the theme preselected.
   *
   * @param {string | undefined} optionValueId - The option value id carried by the event.
   * @returns {boolean}
   */
  #isSizeOptionValue(optionValueId) {
    const position = this.dataset.sizeOptionPosition;
    const productId = this.dataset.productId;
    if (!optionValueId || !position || !productId) return false;

    const input = document.querySelector(
      `variant-picker[data-product-id="${CSS.escape(productId)}"] input[data-option-value-id="${CSS.escape(
        optionValueId
      )}"]`
    );

    if (!(input instanceof HTMLInputElement)) return false;

    return Boolean(input.dataset.inputId?.startsWith(`${position}-`));
  }

  /**
   * Keeps the variant the drawer is opened on in step with the page.
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    this.dataset.currentVariantId = event.detail.resource?.id ?? '';
  };
}

if (!customElements.get('size-drawer-cta')) {
  customElements.define('size-drawer-cta', SizeDrawerCtaComponent);
}
